import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import fr from "../locales/fr.json";
import en from "../locales/en.json";
import es from "../locales/es.json";

import { isMarketingPath, langFromPath } from "../marketing/content/routes";

const STORAGE_KEY = "ownerflow-lang";
const SUPPORTED_LANGS = ["fr", "en", "es"] as const;

type SupportedLang = (typeof SUPPORTED_LANGS)[number];

function isSupportedLang(code: string): code is SupportedLang {
  return SUPPORTED_LANGS.includes(code as SupportedLang);
}

export function detectBrowserLanguage(): SupportedLang {
  if (typeof navigator === "undefined") {
    return "es";
  }

  const candidates = [...(navigator.languages || []), navigator.language].filter(Boolean);
  for (const lang of candidates) {
    const code = lang.split("-")[0].toLowerCase();
    if (isSupportedLang(code)) {
      return code;
    }
  }

  return "es";
}

function getInitialLanguage(): string {
  if (typeof window !== "undefined" && isMarketingPath(window.location.pathname)) {
    const fromPath = langFromPath(window.location.pathname);
    persistLanguage(fromPath);
    return fromPath;
  }

  if (typeof localStorage !== "undefined") {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && isSupportedLang(stored)) {
      return stored;
    }
  }

  const detected = detectBrowserLanguage();
  persistLanguage(detected);
  return detected;
}

export function persistLanguage(lang: string): void {
  if (isSupportedLang(lang) && typeof localStorage !== "undefined") {
    localStorage.setItem(STORAGE_KEY, lang);
  }
}

const LOCALE_MAP: Record<string, string> = {
  fr: "fr-FR",
  en: "en-US",
  es: "es-ES",
};

i18n.use(initReactI18next).init({
  resources: {
    fr: { translation: fr },
    en: { translation: en },
    es: { translation: es },
  },
  lng: getInitialLanguage(),
  fallbackLng: "es",
  interpolation: { escapeValue: false },
});

export function getLocale(): string {
  return LOCALE_MAP[i18n.language] || "es-ES";
}

export default i18n;
